/**
 * Provider 抽象层类型契约（T03）。
 *
 * 唯一事实来源：docs/CODE-TASKS.md 第 5 节「核心契约」。
 *
 * 说明：下方「共享领域类型」小节与 `src/core/types.ts`（T02）字段完全一致，
 * 是同一条契约在本模块内的投影，结构上互相兼容。T02 合并后，只需把该小节
 * 替换为 `export type { ... } from '../types';`，其余代码无需改动。
 */

/* ------------------------------------------------------------------ *
 * 共享领域类型（投影，字段与 docs/CODE-TASKS.md 第 5 节一致）
 * ------------------------------------------------------------------ */

export type Role = 'system' | 'user' | 'assistant' | 'tool';

export interface Attachment {
  id: string;
  kind: 'image' | 'file' | 'audio';
  name: string;
  mime: string;
  size: number;
  dataUrl?: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: Role;
  content: string;
  createdAt: number;
  reasoning?: string;
  attachments?: Attachment[];
  images?: string[];
  audioUrl?: string;
  status: 'pending' | 'streaming' | 'done' | 'error';
  toolCallId?: string;
  toolCalls?: { id: string; name: string; arguments: string }[];
}

export interface ModelSettings {
  maxContext: number;
  thinkingLevel: 'off' | 'low' | 'medium' | 'high';
  temperature: number;
  topP: number;
  maxOutputTokens: number;
  stream: boolean;
  systemPrompt?: string;
}

export interface ToolSpec {
  name: string;
  description: string;
  inputSchema: unknown;
}

/* ------------------------------------------------------------------ *
 * Provider 契约
 * ------------------------------------------------------------------ */

export type ProviderKind = 'openai' | 'anthropic' | 'gemini' | 'custom';
export type ProviderPurpose = 'chat' | 'image' | 'tts';

export interface ProviderConfig {
  id: string;
  kind: ProviderKind;
  protocol?: 'chat-completions' | 'responses' | 'anthropic' | 'gemini';
  vendorId?: string;
  logo?: string;
  name: string;
  baseUrl: string;
  apiKeyRef: string;
  /** What this provider is used for. Missing values are legacy chat providers. */
  purpose?: ProviderPurpose;
  defaultModel?: string;
  enabled: boolean;
}

export function providerPurpose(provider: Pick<ProviderConfig, 'purpose'>): ProviderPurpose {
  return provider.purpose === 'image' || provider.purpose === 'tts' ? provider.purpose : 'chat';
}

/** 运行期已解析出凭据的配置（apiKeyRef -> apiKey 由上层密钥存储负责）。 */
export interface ResolvedProviderConfig extends ProviderConfig {
  apiKey?: string;
  /** 追加到默认请求头上的自定义头（例如 OpenRouter 的 HTTP-Referer）。 */
  headers?: Record<string, string>;
  /** 覆盖适配器默认超时（毫秒）。 */
  timeoutMs?: number;
  /** 覆盖适配器默认重试次数（不含首次请求，默认 1）。 */
  retries?: number;
  /** 覆盖适配器默认重试间隔（毫秒）。 */
  retryDelayMs?: number;
}

export interface IntimacyContext {
  expectedAccountId?: string;
  presetId: string;
  /** Automatic greetings may use the relationship but must not extend user activity retention. */
  activity?: 'user' | 'automation';
  interactions?: { postId: string; liked: boolean; comments: string[]; content?: string }[];
}

export interface ChatRequest {
  messages: ChatMessage[];
  settings: ModelSettings;
  model: string;
  signal?: AbortSignal;
  tools?: ToolSpec[];
  intimacy?: IntimacyContext;
}

export interface StreamUsage {
  promptTokens: number;
  completionTokens: number;
}

export interface StreamChunk {
  type: 'text' | 'reasoning' | 'tool_call' | 'done' | 'error';
  delta?: string;
  error?: string;
  usage?: StreamUsage;
  /** 扩展（可选）：错误分类，便于上层区分超时 / 取消 / 网络错误。 */
  code?: ProviderErrorCode;
  /** 官方模型服务端业务错误码，不影响本地 Provider 的既有错误码契约。 */
  serverCode?: ServerProviderErrorCode;
  /** 扩展（可选）：HTTP 状态码。 */
  status?: number;
}

export interface ProviderAdapter {
  readonly id: string;
  readonly kind: ProviderKind;
  listModels(): Promise<string[]>;
  /**
   * 统一的对话入口。
   *
   * 约定：
   * - `settings.stream === true` 时按 SSE 增量产出 `text` / `reasoning` / `tool_call`，
   *   结束时产出一次 `done`；`false` 时一次性产出文本再产出 `done`。
   * - HTTP 错误、超时、网络错误一律规范化为 `{ type: 'error' }` 分片，**不抛异常**。
   * - 调用方通过 `ChatRequest.signal` 取消时，适配器**静默结束迭代**：中止底层请求、
   *   释放 response body，并且**不产出任何分片（包括 `error`）**。上层应以
   *   `signal.aborted` 判定取消，而不是等待 `error` 分片。
   * - 服务端发完 `[DONE]` 后若仍保持连接（keep-alive 常见行为），适配器同样会
   *   主动释放 body 与底层请求，不留悬挂连接。
   */
  chat(req: ChatRequest): AsyncIterable<StreamChunk>;
}

export type ProviderErrorCode =
  | 'http'
  | 'timeout'
  | 'network'
  | 'aborted'
  | 'parse'
  | 'config'
  | 'unsupported'
  | 'unknown';

export type ServerProviderErrorCode =
  | 'unauthorized'
  | 'insufficient_balance'
  | 'model_unavailable'
  | 'billing_error'
  | 'rate_limited'
  | 'service_unavailable';

/** 可注入的 fetch 实现（默认取 globalThis.fetch）。 */
export type FetchLike = (input: string | URL, init?: RequestInit) => Promise<Response>;

/** 适配器可复用的运行期依赖（便于测试与自定义适配器共享同一套超时/重试参数）。 */
export interface ProviderDeps {
  /** Durable jobs negotiate on the server; observers must never resubmit variants. */
  negotiateCompatibility?: boolean;
  fetch?: FetchLike;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  extraHeaders?: Record<string, string>;
}

/** 适配器工厂：注册表按 kind 实例化适配器。 */
export type ProviderAdapterFactory = (
  config: ResolvedProviderConfig,
  deps?: ProviderDeps,
) => ProviderAdapter;
